import { useCallback, useState } from 'react';
import { useApiData, useAuthenticatedFetch, getApiUrl } from '../client';
import { useAccessToken } from '../../auth/hooks';
import type { RunExecution, ReflectionItem, SimulationListResponse } from '../../types/media-sim';

export function useStartExecution() {
  const { fetchData, loading } = useAuthenticatedFetch<RunExecution>();
  const startExecution = useCallback(
    (projectId: string, runId: string, executionType: 'sample' | 'full') =>
      fetchData(`/media-sim/projects/${projectId}/runs/${runId}/execute`, {
        method: 'POST',
        body: JSON.stringify({ execution_type: executionType }),
      }),
    [fetchData]
  );
  return { startExecution, loading };
}

interface ExecutionSummary {
  id: string;
  run_id: string;
  execution_type: 'sample' | 'full';
  status: string;
  progress_percent: number;
}

export function useRunExecutions(projectId: string, runId: string) {
  const { data, loading, error, refetch } = useApiData<ExecutionSummary[]>(
    projectId && runId
      ? `/media-sim/projects/${projectId}/runs/${runId}/executions`
      : ''
  );
  return { executions: data ?? [], loading, error, refetch };
}

export function useExecution(projectId: string, runId: string, executionId: string) {
  const { data, loading, error, refetch } = useApiData<RunExecution>(
    executionId
      ? `/media-sim/projects/${projectId}/runs/${runId}/executions/${executionId}`
      : ''
  );
  return { execution: data, loading, error, refetch };
}

export function useSimulationResults(
  projectId: string,
  runId: string,
  executionId: string,
  options?: { editorDecision?: string; limit?: number }
) {
  const params = new URLSearchParams();
  if (options?.editorDecision) params.set('editor_decision', options.editorDecision);
  if (options?.limit) params.set('limit', String(options.limit));
  const qs = params.toString();
  const endpoint = executionId
    ? `/media-sim/projects/${projectId}/runs/${runId}/executions/${executionId}/simulations${qs ? `?${qs}` : ''}`
    : '';
  const { data, loading, error, refetch } = useApiData<SimulationListResponse>(endpoint);
  return { results: data, loading, error, refetch };
}

export function useCancelExecution() {
  const { fetchData, loading } = useAuthenticatedFetch<{ status: string }>();
  const cancel = useCallback(
    (projectId: string, runId: string, executionId: string) =>
      fetchData(
        `/media-sim/projects/${projectId}/runs/${runId}/executions/${executionId}/cancel`,
        { method: 'POST' }
      ),
    [fetchData]
  );
  return { cancel, loading };
}

export function useRetryExecution() {
  const { fetchData, loading } = useAuthenticatedFetch<{ status: string }>();
  const retry = useCallback(
    (projectId: string, runId: string, executionId: string) =>
      fetchData(
        `/media-sim/projects/${projectId}/runs/${runId}/executions/${executionId}/retry`,
        { method: 'POST' }
      ),
    [fetchData]
  );
  return { retry, loading };
}

export function useExecutionReflections(
  projectId: string,
  runId: string,
  executionId: string,
  options?: { type?: string; publicationId?: string }
) {
  const params = new URLSearchParams();
  if (options?.type) params.set('type', options.type);
  if (options?.publicationId) params.set('publication_id', options.publicationId);
  const qs = params.toString();
  const endpoint = executionId
    ? `/media-sim/projects/${projectId}/runs/${runId}/executions/${executionId}/reflections${qs ? `?${qs}` : ''}`
    : '';
  const { data, loading, error, refetch } = useApiData<{ reflections: ReflectionItem[] }>(endpoint);
  return { reflections: data?.reflections ?? [], loading, error, refetch };
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function useExportSimulations() {
  const token = useAccessToken();
  const [loading, setLoading] = useState(false);

  const downloadExport = useCallback(
    async (
      projectId: string,
      runId: string,
      executionId: string,
      format: 'xlsx' | 'parquet' = 'xlsx',
      editorDecision: string = 'all'
    ) => {
      setLoading(true);
      try {
        const apiUrl = getApiUrl();
        const params = new URLSearchParams({ format, editor_decision: editorDecision });
        const url = `${apiUrl}/media-sim/projects/${projectId}/runs/${runId}/executions/${executionId}/simulations/export/?${params}`;
        const res = await fetch(url, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: 'application/octet-stream',
          },
        });
        if (!res.ok) throw new Error('Export failed');
        const arrayBuffer = await res.arrayBuffer();
        const mimeType = format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/octet-stream';
        const blob = new Blob([arrayBuffer], { type: mimeType });
        const ext = format === 'parquet' ? 'parquet' : 'xlsx';
        triggerDownload(blob, `simulation-results-${executionId}.${ext}`);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  return { downloadExport, loading };
}

export function useExportReflections() {
  const token = useAccessToken();
  const [loading, setLoading] = useState(false);

  const downloadExport = useCallback(
    async (
      projectId: string,
      runId: string,
      executionId: string,
      format: 'xlsx' | 'parquet' = 'xlsx'
    ) => {
      setLoading(true);
      try {
        const apiUrl = getApiUrl();
        const params = new URLSearchParams({ format });
        const url = `${apiUrl}/media-sim/projects/${projectId}/runs/${runId}/executions/${executionId}/reflections/export/?${params}`;
        const res = await fetch(url, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            Accept: 'application/octet-stream',
          },
        });
        if (!res.ok) throw new Error('Export failed');
        const arrayBuffer = await res.arrayBuffer();
        const mimeType = format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/octet-stream';
        const blob = new Blob([arrayBuffer], { type: mimeType });
        const ext = format === 'parquet' ? 'parquet' : 'xlsx';
        triggerDownload(blob, `reflection-notes-${executionId}.${ext}`);
      } finally {
        setLoading(false);
      }
    },
    [token]
  );

  return { downloadExport, loading };
}
